import { useMemo, useState } from "react";
import { TrendingUp, IndianRupee, Calendar, ArrowRight, Sparkles, Share2 } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Public earnings calculator — illustrative only.
 * Math:  expected profit = invested × (annualReturnRate / 12) × months × growthFactor
 * We use a simple linear estimator anchored to ~25%/year ≈ 2.08%/month, with a livestock
 * "growth bonus" that slightly boosts longer holds (animals gain weight non-linearly).
 */
export default function EarningsCalculator() {
  const [amount, setAmount] = useState(100000);   // ₹
  const [months, setMonths] = useState(5);

  const result = useMemo(() => {
    const baseRate = 0.0225;                       // ~27%/year base on capital
    const growthFactor = 1 + (months - 4) * 0.05;  // a touch better for longer holds
    const profit = Math.round(amount * baseRate * months * growthFactor);
    const total = amount + profit;
    const monthly = Math.round(profit / months);
    const roi = ((profit / amount) * 100).toFixed(1);
    const platformFee = Math.round(profit * 0.05);
    const net = profit - platformFee;
    return { profit, total, monthly, roi, platformFee, net };
  }, [amount, months]);

  const fmt = (n) => `₹${n.toLocaleString("en-IN")}`;

  const shareWhatsApp = () => {
    const url = "https://passiveiearn.com/register";
    const e = String.fromCodePoint;
    const sheep = e(0x1F411), money = e(0x1F4B0), cal = e(0x1F4C5),
          chart = e(0x1F4C8), spark = e(0x2728), point = e(0x1F449);
    const text =
      `${sheep} I just simulated my returns on PassiveIearn!\n\n` +
      `${money} Investment: ${fmt(amount)}\n` +
      `${cal} Period: ${months} months\n` +
      `${chart} Estimated payout: ${fmt(result.total)}\n` +
      `${spark} Net profit: ${fmt(result.net)} (${result.roi}% ROI)\n\n` +
      `Try it yourself ${point} ${url}`;
    const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(wa, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="relative overflow-hidden">
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#D71921]/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#B8862A]/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-2xl mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-3 border"
            style={{ background: "linear-gradient(90deg, #FCEDED 0%, #FFF5DA 100%)", borderColor: "#F2C97A", color: "#7A1010" }}>
            <Sparkles className="w-3.5 h-3.5" />
            Earnings Calculator
          </div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight">
            See what your money<br />could become.
          </h2>
          <p className="text-[#6B5F58] mt-3 text-base md:text-lg">
            Drag the sliders. We&apos;ll estimate your profit based on average returns from past lots.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 items-stretch">
          {/* LEFT: Sliders */}
          <div className="lg:col-span-3 rounded-3xl bg-white border border-[#E8E0D2] p-6 md:p-10 shadow-xl shadow-[#D71921]/5">
            {/* Amount */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-[#1F1816] flex items-center gap-2">
                  <IndianRupee className="w-4 h-4 text-[#D71921]" /> Investment amount
                </label>
                <span className="font-display text-2xl text-[#D71921]" data-testid="calc-amount-value">{fmt(amount)}</span>
              </div>
              <input data-testid="calc-amount-slider" type="range"
                min={25000} max={500000} step={5000}
                value={amount} onChange={(e) => setAmount(+e.target.value)}
                className="w-full gf-slider" style={sliderStyle} />
              <div className="flex justify-between text-[11px] text-[#6B5F58] mt-2">
                <span>₹25,000</span><span>₹2,50,000</span><span>₹5,00,000</span>
              </div>
            </div>

            {/* Duration */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-[#1F1816] flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#3F6B3F]" /> Holding period
                </label>
                <span className="font-display text-2xl text-[#3F6B3F]" data-testid="calc-months-value">{months} months</span>
              </div>
              <input data-testid="calc-months-slider" type="range"
                min={2} max={9} step={1}
                value={months} onChange={(e) => setMonths(+e.target.value)}
                className="w-full gf-slider" style={sliderStyleGreen} />
              <div className="flex justify-between text-[11px] text-[#6B5F58] mt-2">
                <span>2 mo</span><span>5 mo</span><span>9 mo</span>
              </div>
            </div>

            {/* Mini breakdown chart */}
            <div className="mt-6 rounded-2xl border border-[#E8E0D2] bg-[#F8F4E9] p-5">
              <div className="text-[11px] uppercase tracking-widest text-[#6B5F58] font-bold mb-3">Breakdown</div>
              <BreakdownBar label="Your investment" amount={amount} of={result.total}
                color="linear-gradient(90deg, #1F1816 0%, #3A2925 100%)" />
              <BreakdownBar label="Estimated profit" amount={result.profit} of={result.total}
                color="linear-gradient(90deg, #D71921 0%, #B8862A 100%)" />
              <BreakdownBar label="Platform fee (5%)" amount={result.platformFee} of={result.total}
                color="linear-gradient(90deg, #6B5F58 0%, #B8862A 100%)" muted />
            </div>
          </div>

          {/* RIGHT: Result card */}
          <div className="lg:col-span-2 rounded-3xl p-6 md:p-8 text-white relative overflow-hidden flex flex-col"
            style={{ background: "linear-gradient(160deg, #D71921 0%, #B0151E 50%, #7A1010 100%)" }}>
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#F2C97A]/25 blur-3xl" />
            <div className="absolute -bottom-32 -left-16 w-72 h-72 rounded-full bg-[#3F6B3F]/20 blur-3xl" />

            <div className="relative">
              <div className="text-xs uppercase tracking-widest text-[#F2C97A] font-bold">Estimated payout</div>
              <div className="font-display text-5xl md:text-6xl tracking-tight mt-2" data-testid="calc-total">
                {fmt(result.total)}
              </div>
              <div className="text-sm text-white/80 mt-1">
                in {months} months &middot; {result.roi}% ROI
              </div>
            </div>

            <div className="relative grid grid-cols-2 gap-3 mt-6">
              <Box label="Net profit" value={fmt(result.net)} accent="#F2C97A" />
              <Box label="Per month" value={fmt(result.monthly)} accent="#FFFFFF" />
            </div>

            <div className="relative mt-auto pt-6 space-y-2">
              <Link to="/register" data-testid="calc-cta"
                className="block text-center px-5 py-3.5 rounded-xl bg-white text-[#D71921] font-semibold hover:bg-[#FFF5DA] transition-colors">
                <span className="inline-flex items-center gap-2">Start earning <ArrowRight className="w-4 h-4" /></span>
              </Link>
              <button onClick={shareWhatsApp} data-testid="calc-share-whatsapp"
                className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/15 backdrop-blur border border-white/25 text-white font-medium hover:bg-white/25 transition-colors text-sm">
                <Share2 className="w-4 h-4" /> Share on WhatsApp
              </button>
              <p className="text-[10px] text-white/60 text-center mt-2 leading-relaxed">
                * Illustrative estimate based on past performance. Actual returns vary with market and farm operations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Box({ label, value, accent }) {
  return (
    <div className="bg-white/12 backdrop-blur rounded-xl p-3 border border-white/15">
      <div className="text-[10px] uppercase tracking-widest text-white/60">{label}</div>
      <div className="font-display text-xl mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function BreakdownBar({ label, amount, of, color, muted }) {
  const pct = Math.min(100, Math.max(2, (amount / of) * 100));
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex justify-between text-xs mb-1.5">
        <span className={muted ? "text-[#6B5F58]" : "text-[#1F1816] font-medium"}>{label}</span>
        <span className="font-mono text-[#1F1816]">₹{amount.toLocaleString("en-IN")}</span>
      </div>
      <div className="h-2 rounded-full bg-[#E8E0D2] overflow-hidden">
        <div className="h-full rounded-full transition-all duration-300"
          style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

const sliderStyle = {
  WebkitAppearance: "none",
  height: "6px",
  borderRadius: "9999px",
  background: "linear-gradient(90deg, #D71921 0%, #B8862A 100%)",
  outline: "none",
};
const sliderStyleGreen = {
  ...sliderStyle,
  background: "linear-gradient(90deg, #3F6B3F 0%, #B8862A 100%)",
};
